// app/javascript/block-registry.ts
//
// Registers all @opensite/ui block components with @page-speed/blocks's
// renderer registry. Must be called once before the app renders.
//
// @opensite/ui block _type values correspond to the `id` field in @opensite/ui's
// block registry. Props are deserialized from the JSON __jsx format before
// being forwarded to the component.
import React from "react";
import { registerBlockRenderer } from "@page-speed/blocks";
import { compileMarkdown } from "@page-speed/markdown-to-jsx";
import type { BlockRendererProps } from "@page-speed/blocks/types";
import { DynamicIcon } from "@opensite/ui";
import { loadBlockComponent } from "@opensite/ui/registry/loader";

// Native HTML tags that can appear as __jsx values in serialized payloads.
const HTML_TAGS = new Set([
  "div", "span", "p", "a", "button", "section", "article", "aside",
  "header", "footer", "main", "nav", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "img", "picture", "figure", "figcaption", "br", "hr", "strong", "em",
]);

// Named React components that can appear as __jsx values.
const JSX_COMPONENTS: Record<string, React.ComponentType<any>> = {
  DynamicIcon,
};

// Recursively converts { __jsx, ...props } objects into React elements.
// Plain objects and arrays are walked so nested __jsx values are also resolved.
function deserializeJsx(value: unknown): unknown {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(deserializeJsx);
  }

  const obj = value as Record<string, unknown>;

  if ("__jsx" in obj) {
    const { __jsx: tag, children, ...rest } = obj;
    const type = tag as string;

    const resolvedProps: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      resolvedProps[k] = deserializeJsx(v);
    }

    let resolvedChildren: React.ReactNode[] | undefined;
    if (children !== undefined) {
      const c = deserializeJsx(children);
      resolvedChildren = (Array.isArray(c) ? c : [c]) as React.ReactNode[];
    }

    const component: string | React.ComponentType<any> =
      JSX_COMPONENTS[type] ?? (HTML_TAGS.has(type) ? type : "span");

    return resolvedChildren
      ? React.createElement(component, resolvedProps, ...resolvedChildren)
      : React.createElement(component, resolvedProps);
  }

  // Plain object — deserialize each value. Markup-looking STRING values are
  // compiled to real elements here (key-aware, see below); everything else
  // recurses.
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    result[k] = shouldHydrateMarkupString(k, v)
      ? compileMarkdown(v as string, HYDRATION_MARKDOWN_OPTIONS)
      : deserializeJsx(v);
  }
  return result;
}

// compileMarkdown's defaults do NOT drop <script>/<style> from embedded raw
// HTML (verified against 0.0.7) — they would materialize as real, executable
// elements. The content originates from our own generation pipeline, not end
// users, but a hydration path must not be able to inject executable script.
const HYDRATION_MARKDOWN_OPTIONS = {
  overrides: {
    script: () => null,
    style: () => null,
  },
};

// ReactNode-typed props (e.g. feature-showcase items[].content and
// .mediaComponent) arrive from the generation pipeline as HTML STRINGS — the
// component registry's example_props advertise them that way. React escapes
// string children, so without hydration the page shows literal "<div>…" text.
// Compile markup-looking strings into elements with the same sanitizing
// renderer the blog path uses (raw HTML parsed, <script> stripped). Excluded
// keys: markdown* props are compiled by the components themselves and MUST
// stay strings; *html* props (e.g. contentHtml) are rendered via
// dangerouslySetInnerHTML and must also pass through untouched.
function shouldHydrateMarkupString(key: string, value: unknown): boolean {
  return (
    typeof value === "string" &&
    /^\s*<[a-zA-Z]/.test(value) &&
    !/markdown|html/i.test(key)
  );
}

const lazyComponents = new Map<
  string,
  React.LazyExoticComponent<React.ComponentType<any>>
>();

class BlockErrorBoundary extends React.Component<
  { blockId: string; children?: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    console.error(`Error rendering OpenSite block "${this.props.blockId}":`, error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

function getLazyComponent(id: string) {
  let component = lazyComponents.get(id);
  if (!component) {
    component = React.lazy(async () => ({
      default: await loadBlockComponent(id),
    }));
    lazyComponents.set(id, component);
  }
  return component;
}

export function initOpenSiteRenderers(blockIds: Iterable<string>): void {
  for (const id of blockIds) {
    registerBlockRenderer(id, ({ block }: BlockRendererProps) => {
      const Component = getLazyComponent(id);
      const props = deserializeJsx(
        (block.blockProps ?? {}) as Record<string, unknown>
      ) as Record<string, unknown>;
      return React.createElement(
        BlockErrorBoundary,
        { blockId: id },
        React.createElement(
          React.Suspense,
          { fallback: null },
          React.createElement(Component, props)
        )
      );
    });
  }
}
